import { CountryCode } from 'libphonenumber-js/mobile';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';

import {
  getLocaleConfigForLanguage,
  PhoneLocaleConfig,
  PhoneNumberFormat,
} from '../configs/locale-phone-mapping';
import { getLocaleUnitConfig, getUnitConfigForSystem, UnitSystemConfig } from '../configs/locale-unit-mapping';
import { UnitSystem } from '../types/unit-system';
import { LanguageSelectionService } from './language-selection.service';

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  private readonly _phoneConfig$: BehaviorSubject<PhoneLocaleConfig>;
  private readonly _unitConfig$: BehaviorSubject<UnitSystemConfig>;

  public readonly phoneConfig$: Observable<PhoneLocaleConfig>;

  public readonly phoneCountryCode$: Observable<CountryCode>;

  public readonly phoneFormat$: Observable<PhoneNumberFormat>;

  public readonly phonePlaceholder$: Observable<string>;

  public readonly unitConfig$: Observable<UnitSystemConfig>;

  public readonly unitSystem$: Observable<UnitSystem>;

  constructor(private readonly _languageService: LanguageSelectionService) {
    this._phoneConfig$ = new BehaviorSubject<PhoneLocaleConfig>(getLocaleConfigForLanguage('en-US'));
    this._unitConfig$ = new BehaviorSubject<UnitSystemConfig>(getLocaleUnitConfig('en-US'));
    
    this.phoneConfig$ = this._phoneConfig$.asObservable();
    this.phoneCountryCode$ = this.phoneConfig$.pipe(map((config) => config.country), distinctUntilChanged());
    this.phoneFormat$ = this.phoneConfig$.pipe(map((config) => config.format), distinctUntilChanged());
    this.phonePlaceholder$ = this.phoneConfig$.pipe(map((config) => config.placeholder), distinctUntilChanged());
    this.unitConfig$ = this._unitConfig$.asObservable();
    this.unitSystem$ = this.unitConfig$.pipe(map((config) => config.system), distinctUntilChanged());
    
    this._languageService.language$.subscribe((language) => {
      this._phoneConfig$.next(getLocaleConfigForLanguage(language));
      this._unitConfig$.next(getLocaleUnitConfig(language));
    });
  }

  public getPhoneDefaultCountryCode(): CountryCode {
    return this._phoneConfig$.value.country;
  }

  public getPhoneDefaultFormat(): PhoneNumberFormat {
    return this._phoneConfig$.value.format;
  }

  public getPhonePlaceholder(): string {
    return this._phoneConfig$.value.placeholder;
  }

  public getPhonePriorityCountries(): CountryCode[] {
    return this._phoneConfig$.value.priorityCountries || [];
  }

  public getPhoneConfig(): PhoneLocaleConfig {
    return this._phoneConfig$.value;
  }

  public getCurrentLocale(): string {
    return this._languageService.getCurrentLocale();
  }

  public formatDate(
    value: Date | number | string,
    formatOrOptions?: Intl.DateTimeFormatOptions | string,
    timezone?: string
  ): string {
    const locale = this.getCurrentLocale();
    const dateValue = this._parseDate(value);

    if (!dateValue || isNaN(dateValue.getTime())) {
      return '';
    }

    if (typeof formatOrOptions === 'string') {
      const standardOptions = this._getStandardFormatOptions(formatOrOptions);
      if (standardOptions) {
        return this._formatWithIntl(dateValue, standardOptions, timezone, locale);
      }
      
      const customFormat = this._getCustomFormat(formatOrOptions, locale);
      return this._formatWithDatePipe(dateValue, customFormat, timezone, locale);
    }

    const formatOptions = timezone ? { ...formatOrOptions, timeZone: timezone } : formatOrOptions;

    try {
      return new Intl.DateTimeFormat(locale, formatOptions).format(dateValue);
    } catch (error) {
      return new Intl.DateTimeFormat('en-US', formatOptions).format(dateValue);
    }
  }

  private _getStandardFormatOptions(format: string): Intl.DateTimeFormatOptions | null {
    const formatMap: Record<string, Intl.DateTimeFormatOptions> = {
      short: {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      },
      medium: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      },
      shortDate: {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric',
      },
      mediumDate: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      },
      longDate: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      shortTime: {
        hour: 'numeric',
        minute: '2-digit',
      },
    };

    if (format === 'MMM d') {
      return { month: 'short', day: 'numeric' };
    }

    return formatMap[format] || null;
  }

  private _getCustomFormat(format: string, locale: string): string {
    const monthFirstLocales = ['en-US'];
    const isMonthFirst = monthFirstLocales.some(prefix => locale.startsWith(prefix));
    
    const customFormats: Record<string, { us: string; intl: string }> = {
      timeFirst: {
        us: 'h:mm:ss a • M/d/yy',
        intl: 'HH:mm:ss • d/M/yy',
      },
      timeFirstNoAmPm: {
        us: 'HH:mm:ss • MM/dd/yy',
        intl: 'HH:mm:ss • dd/MM/yy',
      },
      input: {
        us: 'M/d/yyyy, HH:mm:ss',
        intl: 'd/M/yyyy, HH:mm:ss',
      },
      dateTimestamp: {
        us: "yyyy-MM-dd'T'HH:mm:ss",
        intl: "yyyy-MM-dd'T'HH:mm:ss",
      },
    };

    const customFormat = customFormats[format];
    if (customFormat) {
      return isMonthFirst ? customFormat.us : customFormat.intl;
    }

    return format;
  }

  private _formatWithIntl(
    date: Date,
    options: Intl.DateTimeFormatOptions,
    timezone: string | undefined,
    locale: string
  ): string {
    const formatOptions = timezone ? { ...options, timeZone: timezone } : options;

    try {
      return new Intl.DateTimeFormat(locale, formatOptions).format(date);
    } catch (error) {
      return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
    }
  }

  private _formatWithDatePipe(
    date: Date,
    format: string,
    timezone: string | undefined,
    locale: string
  ): string {
    try {
      const datePipe = new DatePipe(locale);
      return datePipe.transform(date, format, timezone) || '';
    } catch (error) {
      const datePipe = new DatePipe('en-US');
      return datePipe.transform(date, format, timezone) || '';
    }
  }

  public formatTime(
    value: Date | number | string,
    options?: Intl.DateTimeFormatOptions
  ): string {
    const defaultOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
    };

    return this.formatDate(value, { ...defaultOptions, ...options });
  }

  public formatDateOnly(
    value: Date | number | string,
    options?: Intl.DateTimeFormatOptions
  ): string {
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };

    return this.formatDate(value, { ...defaultOptions, ...options });
  }

  public getDateInputFormat(): string {
    const locale = this._languageService.getCurrentLocale();
    const testDate = new Date(2025, 0, 15);
    const formatted = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).format(testDate);
    
    const isMonthFirst = formatted.indexOf('1') < formatted.indexOf('15');
    return isMonthFirst ? 'M/D/yyyy, HH:mm:ss' : 'D/M/yyyy, HH:mm:ss';
  }

  private _parseDate(value: Date | number | string): Date {
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? new Date(NaN) : value;
    }

    if (typeof value === 'number') {
      return new Date(value);
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      console.warn(`[LocaleService] Invalid date value: ${value}`);
      return new Date(NaN);
    }

    return parsed;
  }

  public formatSliderValue(value: number): string {
    return this.formatNumber(value, 1, 1);
  }

  public formatCoordinate(value: number): string {
    const locale = this._languageService.getCurrentLocale();
    const formatOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: 6,
      maximumFractionDigits: 6,
    };

    try {
      return new Intl.NumberFormat(locale, formatOptions).format(value);
    } catch (error) {
      return new Intl.NumberFormat('en-US', formatOptions).format(value);
    }
  }

  public formatDecimalSeparators(key: string, value: unknown): string {
    if (value == null || value === '') {
      return '';
    }

    if (typeof value === 'number') {
      const locale = this._languageService.getCurrentLocale();
      const formatOptions: Intl.NumberFormatOptions = {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      };

      try {
        return new Intl.NumberFormat(locale, formatOptions).format(value);
      } catch (error) {
        return new Intl.NumberFormat('en-US', formatOptions).format(value);
      }
    }

    if (typeof value === 'string') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && value.trim() !== '') {
        const locale = this._languageService.getCurrentLocale();
        const formatOptions: Intl.NumberFormatOptions = {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        };

        try {
          return new Intl.NumberFormat(locale, formatOptions).format(numValue);
        } catch (error) {
          return new Intl.NumberFormat('en-US', formatOptions).format(numValue);
        }
      }
    }

    return String(value);
  }

  public formatNumber(value: number, minDecimals = 1, maxDecimals = 1): string {
    const locale = this._languageService.getCurrentLocale();
    const formatOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: maxDecimals,
    };

    try {
      return new Intl.NumberFormat(locale, formatOptions).format(value);
    } catch (error) {
      return new Intl.NumberFormat('en-US', formatOptions).format(value);
    }
  }

  public formatDistanceText(distanceText: string): string {
    if (!distanceText) {
      return '';
    }

    const distanceParts = distanceText.match(/([\d.]+)\s*(.+)/);
    if (!distanceParts) {
      return distanceText;
    }

    const numericValue = parseFloat(distanceParts[1]);
    const unit = distanceParts[2];

    if (isNaN(numericValue)) {
      return distanceText;
    }

    const formattedNumber = this.formatNumber(numericValue, 0, 1);
    return `${formattedNumber} ${unit}`;
  }
  
  public getUnitSystem(): UnitSystem {
    return this._unitConfig$.value.system;
  }

  public getUnitConfig(): UnitSystemConfig {
    return this._unitConfig$.value;
  }

  public setUnitSystem(system: UnitSystem): void {
    if (this._unitConfig$.value.system !== system) {
      this._unitConfig$.next(getUnitConfigForSystem(system));
    }
  }

  public getDistancePrecision(value: number): number {
    return this.getUnitSystem() === 'metric' || value < 1 ? 2 : 0;
  }
}

